import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { BRAND, telLink, waLink } from "../../lib/brand";

const QUICK = [
  ["/", "Home"],
  ["/epoxy-flooring", "Epoxy Flooring"],
  ["/waterproofing", "Waterproofing"],
  ["/painting", "Painting"],
  ["/projects", "Projects"],
  ["/about", "About"],
  ["/faq", "FAQ"],
  ["/contact", "Contact"],
];

const SERVICE_LINKS = [
  ["/epoxy-flooring", "Epoxy Flooring"],
  ["/epoxy-flooring", "Industrial Flooring"],
  ["/epoxy-flooring", "Garage Flooring"],
  ["/waterproofing", "Roof Waterproofing"],
  ["/waterproofing", "Terrace Waterproofing"],
  ["/waterproofing", "Bathroom Waterproofing"],
  ["/painting", "Interior Painting"],
  ["/painting", "Exterior Painting"],
  ["/painting", "Commercial Painting"],
  ["/painting", "Industrial Painting"],
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-ink text-white/85 mt-24 relative grain"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Brand block */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 bg-white text-ink grid place-items-center font-display text-lg">
              U
            </span>
            <div className="font-display text-2xl text-white">{BRAND.name}</div>
          </div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/50 mt-3">
            {BRAND.tagline}
          </div>
          <p className="mt-5 text-white/60 max-w-sm leading-relaxed text-sm">
            Professional epoxy flooring, waterproofing and painting services
            for residential, commercial and industrial properties across
            Gurugram, Manesar, Sohna and Delhi NCR.
          </p>

          <div className="mt-6 flex gap-2">
            <a
              data-testid="footer-instagram"
              href={BRAND.social.instagram}
              target="_blank"
              rel="noreferrer"
              className="p-2.5 border border-white/15 hover:bg-white hover:text-ink transition-colors"
            >
              <Instagram size={14} />
            </a>
            <a
              data-testid="footer-facebook"
              href={BRAND.social.facebook}
              target="_blank"
              rel="noreferrer"
              className="p-2.5 border border-white/15 hover:bg-white hover:text-ink transition-colors"
            >
              <Facebook size={14} />
            </a>
            <a
              data-testid="footer-youtube"
              href={BRAND.social.youtube}
              target="_blank"
              rel="noreferrer"
              className="p-2.5 border border-white/15 hover:bg-white hover:text-ink transition-colors"
            >
              <Youtube size={14} />
            </a>
          </div>
        </div>

        {/* Quick links */}
        <div className="md:col-span-2">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45 mb-5">
            Quick Links
          </div>
          <ul className="space-y-2.5 text-sm">
            {QUICK.map(([to, label]) => (
              <li key={label}>
                <Link
                  to={to}
                  className={`transition-colors ${
                    to === "/epoxy-flooring"
                      ? "text-orange hover:text-orange-light"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div className="md:col-span-3">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45 mb-5">
            Services
          </div>
          <ul className="space-y-2.5 text-sm">
            {SERVICE_LINKS.map(([to, label], i) => (
              <li key={i}>
                <Link
                  to={to}
                  className={`transition-colors ${
                    to === "/epoxy-flooring"
                      ? "text-orange hover:text-orange-light"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-3">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45 mb-5">
            Contact
          </div>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex gap-2 items-start">
              <Phone size={14} className="mt-1 shrink-0" />
              <a href={telLink()} data-testid="footer-phone" className="hover:text-white">
                {BRAND.phone}
              </a>
            </li>
            <li className="flex gap-2 items-start">
              <MessageCircle size={14} className="mt-1 shrink-0" />
              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp"
                className="hover:text-white"
              >
                WhatsApp Us
              </a>
            </li>
            <li className="flex gap-2 items-start">
              <Mail size={14} className="mt-1 shrink-0" />
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="footer-email"
                className="hover:text-white"
              >
                {BRAND.email}
              </a>
            </li>
            <li className="flex gap-2 items-start">
              <MapPin size={14} className="mt-1 shrink-0" />
              <span>{BRAND.city} · {BRAND.hours}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Legal strip */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-5 flex flex-col md:flex-row gap-3 justify-between text-xs text-white/45">
          <div>© {new Date().getFullYear()} {BRAND.name} · {BRAND.domain}</div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-white">
              Terms &amp; Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
